import { forwardRef } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import VerdictBadge from "./VerdictBadge";
import { formatDay, noteAnchor, thesisPath, verdictLabel, whoName } from "./lib/format";
import { proposalLine } from "./lib/verdictForm";

const CONVICTION_MAX = 5;

/** "Watch → Own · 4": a decision's move, with its conviction. */
function decisionLine(event) {
  const conviction =
    event.conviction !== null && event.conviction !== undefined ? ` · ${event.conviction}` : "";
  return `${verdictLabel(event.from_verdict)} → ${verdictLabel(event.to_verdict)}${conviction}`;
}

function historySummary(listed, total) {
  if (total === 0) return "Verdict history — no decisions yet";
  if (listed < total) return `Verdict history — the newest ${listed} of ${total} decisions`;
  return `Verdict history — ${total} ${total === 1 ? "decision" : "decisions"}`;
}

/**
 * The verdict panel (spec §5.2, walkthroughs 3 and 4): the verdict and its
 * conviction, exit condition, primary thesis, who set it when and why; a
 * pending proposal ("Proposed: Own (4) — awaiting your decision") with a
 * way to decide on it; Edit verdict; and the history — the newest
 * decisions with how many there are in all.
 *
 * `onEdit(proposal|null)` opens Edit verdict, pre-filled from the proposal
 * when one is passed. Without it (who's signed in isn't known yet) there
 * are no controls. `listedNotes` says which notes are on the page, so a
 * proposal links to its note only when the link has somewhere to go.
 */
const VerdictPanel = forwardRef(function VerdictPanel(
  { company, thesisTitle, thesisRetired = () => false, listedNotes, onEdit = null },
  headingRef,
) {
  const decided = company.verdict !== "unreviewed";
  const history = Array.isArray(company.verdict_history) ? company.verdict_history : [];
  const total = company.verdict_history_total ?? history.length;
  const latest = history[0];
  const proposal = company.proposal;
  const setDay = formatDay(company.verdict_at);
  const hasConviction = company.conviction !== null && company.conviction !== undefined;

  return (
    <section aria-labelledby="investing-verdict" className="rounded-lg border bg-card p-4">
      <h2 id="investing-verdict" ref={headingRef} tabIndex={-1} className="mt-0 mb-3 text-lg font-semibold outline-none">
        Verdict
      </h2>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <VerdictBadge verdict={company.verdict} conviction={company.conviction} className="text-sm" />
        {decided && hasConviction && (
          <span className="text-sm text-muted-foreground">
            Conviction {company.conviction} of {CONVICTION_MAX}
          </span>
        )}
      </div>

      {decided ? (
        <>
          <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
            <dt className="font-medium text-muted-foreground">Exit condition</dt>
            <dd className="m-0 wrap-anywhere">{company.exit_condition || "None set"}</dd>
            <dt className="font-medium text-muted-foreground">Primary thesis</dt>
            <dd className="m-0">
              {company.primary_thesis ? (
                <Link
                  to={thesisPath(company.primary_thesis)}
                  className="inline-flex min-h-11 items-center underline underline-offset-2 wrap-anywhere md:min-h-0"
                >
                  {thesisTitle(company.primary_thesis)}
                </Link>
              ) : (
                "None"
              )}
            </dd>
            {latest?.reason && (
              <>
                <dt className="font-medium text-muted-foreground">Why</dt>
                <dd className="m-0 wrap-anywhere">{latest.reason}</dd>
              </>
            )}
          </dl>
          <p className="mt-2 mb-0 text-sm text-muted-foreground">
            Set
            {company.verdict_by ? ` by ${company.verdict_by.username}` : ""}
            {setDay ? ` on ${setDay}` : ""}.
          </p>
        </>
      ) : (
        <p className="m-0 text-sm">
          Not decided yet. Research proposes a verdict in its note; the decision is yours.
        </p>
      )}

      {proposal && (
        <div className="mt-4 rounded-md border border-warning bg-warning/10 p-3 text-sm">
          <p className="m-0 font-semibold">{proposalLine(proposal)} — awaiting your decision</p>
          <p className="m-0 text-muted-foreground">
            by {whoName(proposal.by)}
            {proposal.at ? ` on ${formatDay(proposal.at)}` : ""} in{" "}
            {listedNotes.has(proposal.note) ? (
              <a href={`#${noteAnchor(proposal.note)}`} className="underline underline-offset-2">
                note #{proposal.note}
              </a>
            ) : (
              `note #${proposal.note} (older than the notes listed)`
            )}
          </p>
          {proposal.exit_condition && (
            <p className="m-0 wrap-anywhere">Exit condition: {proposal.exit_condition}</p>
          )}
          {proposal.primary_thesis && (
            <p className="m-0 wrap-anywhere">
              Primary thesis: {thesisTitle(proposal.primary_thesis)}
              {/* Said before deciding, not only when the save is refused. */}
              {thesisRetired(proposal.primary_thesis) ? " (retired — pick an active thesis when you decide)" : ""}
            </p>
          )}
          {onEdit && (
            <Button type="button" className="mt-2 h-11 md:h-8" size="sm" onClick={() => onEdit(proposal)}>
              Decide on this proposal
            </Button>
          )}
        </div>
      )}

      {onEdit && (
        <Button type="button" variant="outline" className="mt-4 h-11 md:h-9" onClick={() => onEdit(null)}>
          {decided ? "Edit verdict" : "Set the verdict"}
        </Button>
      )}

      {total === 0 ? (
        <p className="mt-4 mb-0 text-sm text-muted-foreground">{historySummary(0, 0)}.</p>
      ) : (
        <details className="group mt-4">
          <summary className="flex min-h-11 cursor-pointer items-center gap-1 text-sm font-medium md:min-h-8">
            <ChevronRight
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
            />
            {historySummary(history.length, total)}
          </summary>
          <ol className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
            {history.map((event) => (
              <li key={event.id} className="rounded-md border border-border px-3 py-2 text-sm">
                <p className="m-0 font-medium">{decisionLine(event)}</p>
                <p className="m-0 wrap-anywhere">{event.reason}</p>
                {event.exit_condition && (
                  <p className="m-0 wrap-anywhere text-muted-foreground">Exit condition: {event.exit_condition}</p>
                )}
                {event.primary_thesis && (
                  <p className="m-0 text-muted-foreground wrap-anywhere">
                    Primary thesis: {thesisTitle(event.primary_thesis)}
                  </p>
                )}
                <p className="m-0 text-muted-foreground">
                  {whoName(event.by)}, {formatDay(event.at)}
                </p>
              </li>
            ))}
          </ol>
          {history.length < total && (
            <p className="mt-2 mb-0 text-xs text-muted-foreground">
              The page lists the newest {history.length}; the {total - history.length} older ones are kept in
              the record.
            </p>
          )}
        </details>
      )}
    </section>
  );
});

VerdictPanel.propTypes = {
  company: PropTypes.shape({
    verdict: PropTypes.string.isRequired,
    conviction: PropTypes.number,
    exit_condition: PropTypes.string,
    primary_thesis: PropTypes.string,
    verdict_by: PropTypes.shape({ username: PropTypes.string }),
    verdict_at: PropTypes.string,
    proposal: PropTypes.object,
    verdict_history: PropTypes.array,
    verdict_history_total: PropTypes.number,
  }).isRequired,
  thesisTitle: PropTypes.func.isRequired,
  /** Whether a thesis (by slug) is retired: a proposal resting on one says so. */
  thesisRetired: PropTypes.func,
  /** The ids of the notes listed on the page. */
  listedNotes: PropTypes.instanceOf(Set).isRequired,
  /** `(proposal|null)`: open Edit verdict; absent, no controls. */
  onEdit: PropTypes.func,
};

export default VerdictPanel;
